import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { usePageTitle } from "@/hooks/use-page-title"
import { HeroSection } from "@/components/use-cases/hero-section"
import { CategoryTabs } from "@/components/use-cases/category-tabs"
import { UseCaseCard } from "@/components/use-cases/use-case-card"
import { CtaSection } from "@/components/use-cases/cta-section"
import { useCasesBasic, useCaseCategories } from "@/data/use-cases-data"
import { FooterSection } from "@/components/home/footer"

/**
 * Use Cases listing page.
 * Displays all use cases with category filtering via URL query params.
 */
export function UseCasesPage() {
  usePageTitle("Use Cases")
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get("category")

  const [activeCategory, setActiveCategory] = useState<string | null>(
    categoryParam && useCaseCategories.some((c) => c.id === categoryParam)
      ? categoryParam
      : null
  )

  // Sync URL params with state
  useEffect(() => {
    if (categoryParam && useCaseCategories.some((c) => c.id === categoryParam)) {
      setActiveCategory(categoryParam)
    } else if (!categoryParam) {
      setActiveCategory(null)
    }
  }, [categoryParam])

  /**
   * Handles category selection and updates URL params.
   */
  const handleCategoryChange = (categoryId: string | null) => {
    setActiveCategory(categoryId)
    if (categoryId) {
      setSearchParams({ category: categoryId })
    } else {
      setSearchParams({})
    }
  }

  // Filter use cases based on active category
  const filteredUseCases = activeCategory
    ? useCasesBasic.filter((uc) => uc.categoryId === activeCategory)
    : useCasesBasic

  // Get category label for display
  const activeCategoryLabel = activeCategory
    ? useCaseCategories.find((c) => c.id === activeCategory)?.label
    : "All Categories"

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <HeroSection />

      {/* Main Content */}
      <section className="relative overflow-hidden bg-gradient-to-b from-neutral-50 to-white py-16 lg:py-24">
        {/* Background decoration */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-20 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -right-40 bottom-20 h-80 w-80 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Category Tabs */}
          <div className="mb-10">
            <CategoryTabs
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
            />
          </div>

          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-neutral-600">
              Showing <span className="font-medium">{filteredUseCases.length}</span> use
              cases in <span className="font-medium">{activeCategoryLabel}</span>
            </p>
          </div>

          {/* Use Cases Grid */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredUseCases.map((useCase, index) => (
              <UseCaseCard key={useCase.id} useCase={useCase} index={index} />
            ))}
          </div>

          {/* Empty State */}
          {filteredUseCases.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg text-neutral-600">
                No use cases found for this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection />

      {/* Footer */}
      <FooterSection />
    </div>
  )
}
